class Header extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.classList.contains('header-wrapper') ? this : this.querySelector('.header-wrapper');
    this.mobileMenu = document.getElementById('MobileMenu');
    this.menuButton = this.querySelector('button[aria-controls="MobileMenu"]');

    // Defensive check
    if (this.mobileMenu) {
      this.closeButton = this.mobileMenu.querySelector('.mobile-menu-close');
    }

    this.logo = this.querySelector('.header__heading-logo');
    this.logoWrapper = this.querySelector('.header__heading-logo-wrapper');

    this.headerContainer = this.querySelector('.header-container');
    this.mainContent = document.getElementById('MainContent');

    this.initSticky();
    this.initMobileMenu();
    this.initResizeObserver();
    this.initCartSubscriber();
    this.initPasswordDynamicContent();
    this.initNavigationReset();
  }

  initNavigationReset() {
    // Store the original theme-provided color scheme for restoration
    this.originalColorScheme = Array.from(this.classList).find(cls => cls.startsWith('color-scheme-'));

    // Listen for AJAX page loads from Router.js
    window.addEventListener('page:loaded', () => {
      this.resetPasswordState();
      this.updateHeaderHeight(); // Re-evaluate sticky/floating state after router sync
    });
  }

  resetPasswordState() {
    // 1. Remove inline background overrides from password dynamic content
    document.documentElement.style.removeProperty('background-color');
    document.body.style.removeProperty('background-color');
    const mainContent = document.getElementById('MainContent');
    if (mainContent) mainContent.style.removeProperty('background-color');

    // 2. Remove all dynamic state classes
    const targets = [document.documentElement, document.body, this, document.querySelector('footer-root')];

    targets.forEach(target => {
      if (!target) return;
      if (target !== document.documentElement) {
        target.classList.remove('has-dynamic-content');
      }

      // 3. Restore original color schemes
      if (target === document.documentElement || target === document.body) {
        Array.from(target.classList).forEach(cls => {
          if (cls.startsWith('color-scheme-')) target.classList.remove(cls);
        });
      } else {
        const original = target === this ? this.originalColorScheme : target.dataset.originalColorScheme;
        if (original) {
          Array.from(target.classList).forEach(cls => {
            if (cls.startsWith('color-scheme-')) target.classList.remove(cls);
          });
          target.classList.add(original);
        }
      }
    });

    // 3. Clear active trigger states
    document.querySelectorAll('[data-password-trigger]').forEach(t => t.classList.remove('is-active'));
  }

  initCartSubscriber() {
    const updateCartLinks = (cart) => {
      const cartLinks = document.querySelectorAll('.cart-link-text');
      cartLinks.forEach(link => {
        const desktopSpan = link.querySelector('#cart-icon-bubble-count');
        const mobileSpan = link.querySelector('#cart-icon-bubble-count-mobile');
        const drawerSpan = link.querySelector('#cart-icon-bubble-count-drawer');
        const countText = cart.item_count > 0 ? `(${cart.item_count})` : '';

        if (desktopSpan) desktopSpan.textContent = countText;
        if (mobileSpan) mobileSpan.textContent = countText;
        if (drawerSpan) drawerSpan.textContent = countText;
        if (!desktopSpan && !mobileSpan && !drawerSpan) {
          // Backward compatibility/Fallback
          link.textContent = cart.item_count > 0 ? `CART(${cart.item_count})` : 'CART';
        }
      });
    };

    // Cart count is already server-rendered via Liquid in the header HTML.
    // No need to fetch /cart.js on every page load — it wastes a request
    // and can trigger 429 rate limits.

    // Subscribe to dynamic cart updates (add-to-cart, quantity changes)
    if (window.pubsub) {
      window.pubsub.subscribe('cart-update', updateCartLinks);
    }
  }

  initResizeObserver() {
    if (!this.headerContainer) return;
    this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
    this.resizeObserver.observe(this.headerContainer);
  }

  updateHeaderHeight() {
    if (!this.headerContainer) return;

    // Always report the actual header height for layout calculations (prevents cut-off at bottom)
    const height = this.headerContainer.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${height}px`);

    // Detect if the header is currently in a state that overlaps content
    const isOverlay = this.classList.contains('header-wrapper--overlay') || this.classList.contains('header-wrapper--home-mobile-overlay');
    const isSticky = this.classList.contains('header-wrapper--sticky') || this.dataset.sticky === 'true';

    if (isOverlay || isSticky) {
      document.documentElement.classList.add('header-is-floating');
    } else {
      document.documentElement.classList.remove('header-is-floating');
    }
  }

  initSticky() {
    if (this.dataset.sticky === 'true' && this.mainContent) {
      this.mainContent.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.onScroll(); // Initial check
    }
  }

  onScroll() {
    const scrollTop = this.mainContent ? this.mainContent.scrollTop : 0;

    if (scrollTop > 50) { // Threshold for "sticking"
      if (!this.header.classList.contains('sticky-header')) {
        this.header.classList.add('sticky-header');
        this.updateLogoSize(true);
      }
    } else {
      if (this.header.classList.contains('sticky-header')) {
        this.header.classList.remove('sticky-header');
        this.updateLogoSize(false);
      }
    }
  }

  updateLogoSize(isSticky) {
    // Logo styling is handled via CSS classes, or we can adjust styles here if needed.
    // CSS transitions on .header__heading-logo-wrapper are preferred.
    // We might need to swap classes or just let the sticky-header class handle it via CSS descendant selectors.
  }

  initMobileMenu() {
    if (this.menuButton) {
      this.menuButton.addEventListener('click', this.openMenu.bind(this));
    }
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeMenu.bind(this));
    }

    // Close drawer when preloader appears after a nav link click
    if (this.mobileMenu) {
      const preloader = document.getElementById('preloader');
      this.mobileMenu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          if (preloader) {
            // Watch for the preloader to become active, then close drawer
            const observer = new MutationObserver(() => {
              if (preloader.classList.contains('is-loading')) {
                this.closeMenu();
                observer.disconnect();
              }
            });
            observer.observe(preloader, { attributes: true, attributeFilter: ['class'] });
            // Fallback: close after 500ms if preloader never fires
            setTimeout(() => { this.closeMenu(); observer.disconnect(); }, 500);
          } else {
            this.closeMenu();
          }
        });
      });
    }
  }

  openMenu() {
    this.mobileMenu.classList.add('is-active');
    this.header.classList.add('is-active');
    this.menuButton.setAttribute('aria-expanded', 'true');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  }

  closeMenu() {
    if (!this.mobileMenu) return;
    this.mobileMenu.classList.remove('is-active');
    if (this.header) this.header.classList.remove('is-active');
    if (this.menuButton) this.menuButton.setAttribute('aria-expanded', 'false');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  initPasswordDynamicContent() {
    // Ensure we only attach the listener once globally to the document
    if (window.passwordContentInit) return;
    window.passwordContentInit = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-password-trigger]');
      if (!trigger) return;

      const triggerValue = (trigger.dataset.passwordTrigger || '').trim();
      if (!triggerValue) return;

      // Find template regardless of case/whitespace in data attribute
      const templates = document.querySelectorAll('template[data-password-content]');
      const template = Array.from(templates).find(t => t.dataset.passwordContent.trim() === triggerValue);

      if (template) {
        event.preventDefault();

        // Add active indicators and transfer color scheme to body
        document.querySelectorAll('[data-password-trigger]').forEach(t => t.classList.remove('is-active'));
        trigger.classList.add('is-active');
        document.body.classList.add('has-dynamic-content');

        // Detect the color-scheme class and page background from the trigger (to be applied in Phase 3)
        const colorClass = Array.from(trigger.classList).find(cls => cls.startsWith('color-scheme-'));
        const pageBackgroundRgb = trigger.dataset.pageBackground || '';

        const syncTargets = [document.body, document.querySelector('header-root'), document.querySelector('footer-root')];

        // Add visibility state classes immediately to hide old content
        syncTargets.forEach(target => {
          if (target) {
            target.classList.add('has-dynamic-content');
            // Store original before the very first change if not already stored
            if (target !== document.body && !target.dataset.originalColorScheme) {
              const original = Array.from(target.classList).find(cls => cls.startsWith('color-scheme-'));
              if (original) target.dataset.originalColorScheme = original;
            }
          }
        });

        // Add visibility state classes
        syncTargets.forEach(target => {
          if (target) target.classList.add('has-dynamic-content');
        });

        // Always try to find the ACTIVE header-root in the DOM
        // This prevents issues with stale instances after section re-renders
        const headerElement = document.querySelector('header-root');
        if (headerElement && typeof headerElement.loadPasswordContent === 'function') {
          headerElement.loadPasswordContent(template, colorClass, pageBackgroundRgb);
        } else {
          // Fallback if component is not yet upgraded or missing
          console.warn('Header component not found for content swap');
        }
      }
    });
  }

  async loadPasswordContent(template, colorClass, pageBackgroundRgb) {
    const target = (this.mainContent && this.mainContent.querySelector('.content--wrapper'))
      || document.getElementById('PasswordBody')
      || document.getElementById('MainContentContent')
      || this.mainContent;
    if (!target) return;

    // 1. Show Global Preloader if available
    const router = window.router;
    let counterPromise = Promise.resolve();

    if (router && router.preloader) {
      // Apply target background to preloader so transition doesn't show a beige flash
      const rgb = pageBackgroundRgb && pageBackgroundRgb.trim();
      if (rgb) {
        router.preloader.style.setProperty('background-color', `rgb(${rgb})`, 'important');
      }
      router.togglePreloader(true);
      counterPromise = router.animateCounter();
    } else {
      document.documentElement.classList.add('is-loading-content');
      counterPromise = new Promise(resolve => setTimeout(resolve, 400));
    }

    // 2. Wait for loading animation to reach a good point
    await counterPromise;

    const content = template.content.cloneNode(true);

    // 3. Swap content
    target.innerHTML = '';
    target.appendChild(content);

    // 4. Scroll to top
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
    } else {
      const scrollContainer = this.mainContent || window;
      scrollContainer.scrollTo(0, 0);
    }

    // 5. Apply Color Scheme Synchronization (Delayed to Phase 3)
    if (colorClass) {
      const syncTargets = [document.documentElement, document.body, document.querySelector('header-root'), document.querySelector('footer-root')];
      syncTargets.forEach(target => {
        if (!target) return;
        Array.from(target.classList).forEach(cls => {
          if (cls.startsWith('color-scheme-')) target.classList.remove(cls);
        });
        target.classList.add(colorClass);
      });
    }

    // 6. Force page background via inline style (apply to html, body, AND #MainContent — no CSS variable, only data-page-background)
    const rgb = pageBackgroundRgb && pageBackgroundRgb.trim();
    if (rgb) {
      const bg = `rgb(${rgb})`;
      document.documentElement.style.setProperty('background-color', bg, 'important');
      document.body.style.setProperty('background-color', bg, 'important');
      const mainContent = document.getElementById('MainContent');
      if (mainContent) mainContent.style.setProperty('background-color', bg, 'important');
    }

    // 7. Hide Preloader and reset its background for future full-page loads
    if (router && router.preloader) {
      router.togglePreloader(false);
      router.preloader.style.removeProperty('background-color');
    } else {
      setTimeout(() => {
        document.documentElement.classList.remove('is-loading-content');
      }, 50);
    }

    // Close mobile menu if it was open
    this.closeMenu();

    // Re-init any logic if necessary (e.g. Lenis resize)
    if (window.initLenis) window.initLenis();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

customElements.define('header-root', Header);
